// src/components/shared/GreenRatingCalculatorModal.tsx
import React, { useState, useRef } from "react";
import MotionCard, { MotionButton } from "./MotionCard";
import EPAVehicleSelector from "./EPAVehicleSelector";

interface GreenRatingCalculatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectGroup: (groupNumber: number) => void;
  currentGroupNumber?: number;
}

interface USCalculationInputs {
  // נתוני עיר (FTP75)
  co2_city: number;
  co_city: number;
  hc_city: number;
  nox_city: number;
  pm_city: number;
  // נתוני כביש (HWFE)
  co2_highway: number;
  co_highway: number;
  hc_highway: number;
  nox_highway: number;
  pm_highway: number;
}

interface EUCalculationInputs {
  co2: number; // g/km
  co: number; // mg/km
  hc: number; // mg/km
  nox: number; // mg/km
  pm: number; // mg/km
}

const GreenRatingCalculatorModal: React.FC<GreenRatingCalculatorModalProps> = ({
  isOpen,
  onClose,
  onSelectGroup,
  currentGroupNumber,
}) => {
  const [activeTab, setActiveTab] = useState<"us" | "eu">("us");
  const [calculatedGreenRating, setCalculatedGreenRating] = useState<
    number | null
  >(null);
  const [suggestedGroup, setSuggestedGroup] = useState<{
    group: string;
    description?: string;
  } | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  // נתונים לדוגמה מהקובץ החדש
  const [usInputs, setUsInputs] = useState<USCalculationInputs>({
    // עיר (FTP75)
    co2_city: 400,
    co_city: 0.066,
    hc_city: 0.197,
    nox_city: 0.787,
    pm_city: 0.0,
    // כביש (HWFE)
    co2_highway: 267,
    co_highway: 0.022,
    hc_highway: 0.066,
    nox_highway: 0.0,
    pm_highway: 0.0,
  });

  const [vehicleType, setVehicleType] = useState<"regular" | "hybrid" | "phev">("regular");

  const [euInputs, setEuInputs] = useState<EUCalculationInputs>({
    co2: 117,
    co: 60,
    hc: 10,
    nox: 0,
    pm: 1,
  });
  // Handler for EPA vehicle selection
  const handleEPAVehicleSelect = (data: {
    co2_city: number;
    co_city: number;
    hc_city: number;
    nox_city: number;
    pm_city: number;
    co2_highway: number;
    co_highway: number;
    hc_highway: number;
    nox_highway: number;
    pm_highway: number;
  }) => {
    setUsInputs({
      co2_city: data.co2_city,
      co_city: data.co_city,
      hc_city: data.hc_city,
      nox_city: data.nox_city,
      pm_city: data.pm_city,
      co2_highway: data.co2_highway,
      co_highway: data.co_highway,
      hc_highway: data.hc_highway,
      nox_highway: data.nox_highway,
      pm_highway: data.pm_highway,
    });
  };
  // נוסחה אמריקאית מתוקנת - לפי הקובץ החדש
  const calculateUSGreenRating = (
    inputs: USCalculationInputs,
    vehicleType: string = "regular"
  ): { score: number; details: string } => {
    // מקדמי התאמה לפי סוג רכב
    const adjustmentFactors = {
      regular: 1.481,
      hybrid: 1.374,
      phev: 0.8,
    };

    const adjustmentFactor =
      adjustmentFactors[vehicleType as keyof typeof adjustmentFactors] || 1.481;
    const MILE_TO_KM = 0.621371; // המרה מ-miles ל-km (מעודכן)

    // פונקציה לחישוב משוקלל חכם
    const weighted = (city: number, highway: number): number => {
      const hasCity = !isNaN(city) && city > 0;
      const hasHighway = !isNaN(highway) && highway > 0;

      if (hasCity && hasHighway) return highway * 0.66 + city * 0.34;
      if (hasHighway) return highway;
      if (hasCity) return city;
      return 0;
    };

    // חישוב משוקלל
    const w_co2 = weighted(inputs.co2_city, inputs.co2_highway);
    const w_co = weighted(inputs.co_city, inputs.co_highway);
    const w_hc = weighted(inputs.hc_city, inputs.hc_highway);
    const w_nox = weighted(inputs.nox_city, inputs.nox_highway);
    const w_pm = weighted(inputs.pm_city, inputs.pm_highway);

    // המרה ליחידות ישראליות
    const co2 = adjustmentFactor * MILE_TO_KM * w_co2; // g/km
    const co = adjustmentFactor * MILE_TO_KM * w_co * 1000; // mg/km
    const hc = adjustmentFactor * MILE_TO_KM * w_hc * 1000; // mg/km
    const nox = adjustmentFactor * MILE_TO_KM * w_nox * 1000; // mg/km
    const pm = adjustmentFactor * MILE_TO_KM * w_pm * 1000; // mg/km

    // הנוסחה הישראלית הסופית
    const rawScore =
      ((199 * co2 + 8.772 * hc + 634.694 * pm + 163.465 * nox + 0.412 * co) *
        0.69) /
      100;

    // פירוט החישוב
    const weightExplain =
      !isNaN(inputs.co2_highway) && !isNaN(inputs.co2_city)
        ? "66% Highway + 34% City"
        : !isNaN(inputs.co2_highway)
        ? "Highway בלבד"
        : "City בלבד";

    const details = `EPA | מקדם התאמה: ${adjustmentFactor} | g/mile→g/km: ×${MILE_TO_KM} | שימוש ב-${weightExplain}\nCO₂=${co2.toFixed(
      2
    )} g/km, CO=${co.toFixed(2)} mg/km, HC=${hc.toFixed(
      2
    )} mg/km, NOx=${nox.toFixed(2)} mg/km, PM=${pm.toFixed(2)} mg/km`;

    return {
      score: Math.round(rawScore),
      details: details,
    };
  };
  // נוסחה אירופאית מתוקנת - לפי הקובץ החדש
  const calculateEUGreenRating = (
    inputs: EUCalculationInputs
  ): { score: number; details: string } => {
    const result =
      ((199 * inputs.co2 +
        8.772 * inputs.hc +
        634.694 * inputs.pm +
        163.465 * inputs.nox +
        0.412 * inputs.co) *
        0.69) /
      100;

    const details = `WLTP: ציון = (199×${inputs.co2} + 8.772×${inputs.hc} + 634.694×${inputs.pm} + 163.465×${inputs.nox} + 0.412×${inputs.co}) × 0.69 / 100`;

    return {
      score: Math.round(result),
      details: details,
    };
  };

  // מציאת קבוצה לפי ציון ירוק
  // קביעת קבוצת מס ירוק לפי טבלת המכס (17 קבוצות כולל 15א/15ב/15ג)
  const findGroupByGreenRating = (
    greenRating: number
  ): { group: string; description?: string } => {
    if (greenRating <= 50)
      return { group: "1", description: "רכב נטול פליטות" };
    if (greenRating <= 130)
      return { group: "2", description: "רכב בעל הנעה חלופית" };
    if (greenRating <= 150) return { group: "3" };
    if (greenRating <= 170) return { group: "4" };
    if (greenRating <= 175) return { group: "5" };
    if (greenRating <= 180) return { group: "6" };
    if (greenRating <= 185) return { group: "7" };
    if (greenRating <= 190) return { group: "8" };
    if (greenRating <= 195) return { group: "9" };
    if (greenRating <= 200) return { group: "10" };
    if (greenRating <= 205) return { group: "11" };
    if (greenRating <= 210) return { group: "12" };
    if (greenRating <= 220) return { group: "13" };
    if (greenRating <= 250) return { group: "14" };
    if (greenRating <= 300) return { group: "15א" };
    if (greenRating <= 350) return { group: "15ב" };
    return { group: "15ג" };
  };

  const validateInputs = (): boolean => {
    if (activeTab === "eu") {
      // ולידציה אירופאית - CO₂, CO, PM חובה
      if (!euInputs.co2 || euInputs.co2 <= 0) {
        alert("שדה CO₂ הוא חובה");
        return false;
      }
      if (!euInputs.co || euInputs.co <= 0) {
        alert("שדה CO הוא חובה");
        return false;
      }
      if (!euInputs.pm || euInputs.pm < 0) {
        alert("שדה PM הוא חובה");
        return false;
      }
      return true;
    } else {
      // ולידציה אמריקאית - לפחות CO₂ City או Highway
      if (
        (!usInputs.co2_city || usInputs.co2_city <= 0) &&
        (!usInputs.co2_highway || usInputs.co2_highway <= 0)
      ) {
        alert("יש להזין לפחות CO₂ City או CO₂ Highway");
        return false;
      }
      return true;
    }
  };

  const handleCalculate = () => {
    if (!validateInputs()) return;

    let greenRating: number;

    if (activeTab === "us") {
      const result = calculateUSGreenRating(usInputs, vehicleType);
      greenRating = result.score;
    } else {
      const result = calculateEUGreenRating(euInputs);
      greenRating = result.score;
    }

    setCalculatedGreenRating(greenRating);
    const group = findGroupByGreenRating(greenRating);
    setSuggestedGroup(group);
  };
  const handleSelectGroup = () => {
    if (suggestedGroup !== null) {
      // המר את שם הקבוצה למספר group_number הנכון
      let groupNumber: number;

      if (suggestedGroup.group === "15א") {
        groupNumber = 15;
      } else if (suggestedGroup.group === "15ב") {
        groupNumber = 16;
      } else if (suggestedGroup.group === "15ג") {
        groupNumber = 17;
      } else {
        groupNumber = parseInt(suggestedGroup.group);
      }

      onSelectGroup(groupNumber);
      onClose();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const selectedGroup = suggestedGroup;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleOverlayClick}
      dir="rtl"
    >
      <div
        ref={modalRef}
        className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* כותרת */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              מחשבון ציון ירוק
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
            >
              ×
            </button>
          </div>

          {/* טאבים */}
          <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
            <button
              className={`flex-1 py-2 px-4 rounded-md transition-colors ${
                activeTab === "us"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-800"
              }`}
              onClick={() => setActiveTab("us")}
            >
              שיטה אמריקאית
            </button>
            <button
              className={`flex-1 py-2 px-4 rounded-md transition-colors ${
                activeTab === "eu"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-800"
              }`}
              onClick={() => setActiveTab("eu")}
            >
              שיטה אירופאית
            </button>
          </div>

          {/* טופס הזנת נתונים */}
          <MotionCard className="mb-6">
            <h3 className="text-lg font-semibold mb-4">
              {activeTab === "us"
                ? "נתוני פליטות - שיטה אמריקאית (EPA)"
                : "נתוני פליטות - שיטה אירופאית (WLTP/NEDC)"}
            </h3>

            {activeTab === "us" ? (
              <div className="space-y-6">
                {/* נתוני עיר */}
                {/* EPA Vehicle Selector */}
                <EPAVehicleSelector onSelectVehicle={handleEPAVehicleSelect} />

                {/* בחירת סוג רכב */}
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <label className="block text-sm font-semibold text-yellow-800 mb-2">
                    סוג הרכב (משפיע על מקדם ההמרה)
                  </label>
                  <select
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e.target.value as "regular" | "hybrid" | "phev")}
                    className="w-full p-2 border border-yellow-300 rounded focus:ring-2 focus:ring-yellow-500 bg-white"
                  >
                    <option value="regular">רגיל / בנזין / דיזל (מקדם 1.481)</option>
                    <option value="hybrid">היברידי — HEV / HYBRID / FHEV (מקדם 1.374)</option>
                    <option value="phev">פלאג-אין — PHEV / PLUG-IN (מקדם 0.8)</option>
                  </select>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-3 text-blue-800">
                    נסיעה עירונית (City - FTP75)
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        CO₂ (g/mile)
                      </label>
                      <input
                        type="number"
                        step="0.001"
                        value={usInputs.co2_city}
                        onChange={(e) =>
                          setUsInputs({
                            ...usInputs,
                            co2_city: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        CO (g/mile)
                      </label>
                      <input
                        type="number"
                        step="0.001"
                        value={usInputs.co_city}
                        onChange={(e) =>
                          setUsInputs({
                            ...usInputs,
                            co_city: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        HC (g/mile)
                      </label>
                      <input
                        type="number"
                        step="0.001"
                        value={usInputs.hc_city}
                        onChange={(e) =>
                          setUsInputs({
                            ...usInputs,
                            hc_city: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        NOₓ (g/mile)
                      </label>
                      <input
                        type="number"
                        step="0.001"
                        value={usInputs.nox_city}
                        onChange={(e) =>
                          setUsInputs({
                            ...usInputs,
                            nox_city: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        PM (g/mile)
                      </label>
                      <input
                        type="number"
                        step="0.001"
                        value={usInputs.pm_city}
                        onChange={(e) =>
                          setUsInputs({
                            ...usInputs,
                            pm_city: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* נתוני כביש */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-3 text-green-800">
                    נסיעה בינעירונית (Highway - HWFE)
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        CO₂ (g/mile)
                      </label>
                      <input
                        type="number"
                        step="0.001"
                        value={usInputs.co2_highway}
                        onChange={(e) =>
                          setUsInputs({
                            ...usInputs,
                            co2_highway: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        CO (g/mile)
                      </label>
                      <input
                        type="number"
                        step="0.001"
                        value={usInputs.co_highway}
                        onChange={(e) =>
                          setUsInputs({
                            ...usInputs,
                            co_highway: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        HC (g/mile)
                      </label>
                      <input
                        type="number"
                        step="0.001"
                        value={usInputs.hc_highway}
                        onChange={(e) =>
                          setUsInputs({
                            ...usInputs,
                            hc_highway: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        NOₓ (g/mile)
                      </label>
                      <input
                        type="number"
                        step="0.001"
                        value={usInputs.nox_highway}
                        onChange={(e) =>
                          setUsInputs({
                            ...usInputs,
                            nox_highway: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        PM (g/mile)
                      </label>
                      <input
                        type="number"
                        step="0.001"
                        value={usInputs.pm_highway}
                        onChange={(e) =>
                          setUsInputs({
                            ...usInputs,
                            pm_highway: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="mb-4 text-green-800">
                  הזן נתונים מתעודת ה-CoC (Certificate of Conformity):
                </p>
                <a
                  href="https://youtu.be/ftGiegvw6Yw?si=LtlNwT8W3BmWsd9v"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                  <span className="font-medium">צפה בסרטון הסבר</span>
                </a>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CO₂ (g/km)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={euInputs.co2}
                      onChange={(e) =>
                        setEuInputs({
                          ...euInputs,
                          co2: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CO (mg/km)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={euInputs.co}
                      onChange={(e) =>
                        setEuInputs({
                          ...euInputs,
                          co: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      HC (mg/km)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={euInputs.hc}
                      onChange={(e) =>
                        setEuInputs({
                          ...euInputs,
                          hc: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      NOₓ (mg/km)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={euInputs.nox}
                      onChange={(e) =>
                        setEuInputs({
                          ...euInputs,
                          nox: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      PM (mg/km)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={euInputs.pm}
                      onChange={(e) =>
                        setEuInputs({
                          ...euInputs,
                          pm: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4">
              <MotionButton
                onClick={handleCalculate}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                חשב ציון ירוק
              </MotionButton>
            </div>
          </MotionCard>

          {/* תוצאות */}
          {calculatedGreenRating !== null && (
            <MotionCard className="mb-6">
              <h3 className="text-lg font-semibold mb-4">תוצאות החישוב</h3>
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <div className="text-lg font-medium text-blue-800">
                  ציון ירוק מחושב:{" "}
                  <span className="font-bold">{calculatedGreenRating}</span>
                </div>
              </div>
              {selectedGroup && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-lg font-medium text-green-800 mb-2">
                    קבוצת מיסוי מומלצת:{" "}
                    <span className="font-bold">{selectedGroup.group}</span>
                  </div>
                  <div className="text-sm text-green-700">
                    <div>טווח ציון: מוגדר לפי קבוצה</div>
                    <div>השפעה על מס: מוגדרת לפי קבוצה</div>
                    {selectedGroup.description && (
                      <div>{selectedGroup.description}</div>
                    )}
                  </div>
                </div>
              )}{" "}
            </MotionCard>
          )}

          {/* כפתורי פעולה */}
          <div className="flex gap-3">
            {suggestedGroup && (
              <MotionButton
                onClick={handleSelectGroup}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
              >
                בחר קבוצה זו
              </MotionButton>
            )}
            <MotionButton
              onClick={onClose}
              className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
            >
              סגור
            </MotionButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GreenRatingCalculatorModal;
